import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [['home_odds', 'Home'], ['draw_odds', 'Draw'], ['away_odds', 'Away']];

function OddsRow({ fx, onSaved }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const val = (k) => draft[k] ?? (fx[k] != null ? String(fx[k]) : '');

  const save = async () => {
    setSaving(true);
    try {
      const body = { fixture_id: fx.fixture_id };
      FIELDS.forEach(([k]) => { body[k] = parseFloat(val(k)); });
      const res = await axios.post(`${API_URL}/api/admin/fixture-odds`, body, { withCredentials: true });
      toast.success(`Odds saved for ${fx.home_team} v ${fx.away_team}${res.data.tips_backfilled ? ` · ${res.data.tips_backfilled} tips updated` : ''}`);
      setDraft({});
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save odds');
    }
    setSaving(false);
  };

  const kicked = new Date(fx.match_time) < new Date();
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border border-gray-200 rounded-md" data-testid={`odds-row-${fx.fixture_id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-[#1A1A1A] truncate">{fx.home_team} <span className="text-gray-400">v</span> {fx.away_team}</div>
        <div className="text-xs text-gray-500">
          {new Date(fx.match_time).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
          {' · '}{fx.odds_source === 'manual' ? 'Manual odds' : fx.home_odds ? 'Live odds' : 'No odds yet'}
          {kicked && ' · Kicked off'}
        </div>
      </div>
      <div className="flex items-end gap-2">
        {FIELDS.map(([k, label]) => (
          <label key={k} className="flex flex-col text-[10px] uppercase tracking-wider text-gray-500">
            {label}
            <Input type="number" step="0.01" min="1.01" className="w-20 text-center" value={val(k)}
              onChange={(e) => setDraft((p) => ({ ...p, [k]: e.target.value }))}
              data-testid={`odds-${k}-${fx.fixture_id}`} />
          </label>
        ))}
        <Button size="sm" onClick={save} disabled={saving} data-testid={`odds-save-${fx.fixture_id}`}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function ManualOddsPanel() {
  const [sport, setSport] = useState('EPL');
  const [rounds, setRounds] = useState([]);
  const [round, setRound] = useState(null);
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setRound(null);
    axios.get(`${API_URL}/api/fixtures/${sport}/rounds`).then((r) => setRounds(r.data || []));
  }, [sport]);

  useEffect(() => {
    if (round !== null || rounds.length === 0) return;
    // Default to the earliest round that still has an unplayed game.
    Promise.all(rounds.map((r) => axios.get(`${API_URL}/api/fixtures/${sport}`, { params: { round_number: r } })))
      .then((all) => {
        const idx = all.findIndex((res) => (res.data || []).some((f) => !f.is_completed && new Date(f.match_time) > new Date()));
        setRound(rounds[idx >= 0 ? idx : rounds.length - 1]);
      });
  }, [rounds, round, sport]);

  const load = async () => {
    if (round === null) return;
    setLoading(true);
    const res = await axios.get(`${API_URL}/api/fixtures/${sport}`, { params: { round_number: round } });
    setFixtures(res.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [round]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="tipster-card space-y-4" data-testid="admin-manual-odds">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold uppercase tracking-tight">Manual Odds</h3>
          <p className="text-sm text-gray-500 mt-1 max-w-xl">
            Enter home/draw/away odds when the odds service is out. Tips already placed keep the odds they were made at;
            tips placed with no odds pick up yours. Live odds replace these when the service is back.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={sport} onValueChange={setSport}>
            <SelectTrigger className="w-32 bg-white" data-testid="odds-sport-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="EPL">EPL</SelectItem>
              <SelectItem value="AL">A-League</SelectItem>
            </SelectContent>
          </Select>
          <Select value={round !== null ? String(round) : undefined} onValueChange={(v) => setRound(parseInt(v, 10))}>
            <SelectTrigger className="w-32 bg-white" data-testid="odds-round-select"><SelectValue placeholder="Round" /></SelectTrigger>
            <SelectContent className="bg-white max-h-72">
              {rounds.map((r) => <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="odds-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>
      ) : fixtures.length === 0 ? (
        <div className="text-sm text-gray-500 py-6 text-center">No fixtures in this round.</div>
      ) : (
        <div className="space-y-2">
          {fixtures.map((fx) => <OddsRow key={`${fx.fixture_id}-${fx.odds_updated_at}`} fx={fx} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
